import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Tabs, TabsContent } from "../components/ui/tabs";
import OverviewTab from "../components/book/OverviewTab";
import UploadTab from "../components/book/UploadTab";
import ManuscriptTab from "../components/book/ManuscriptTab";
import ProofreadTab from "../components/book/ProofreadTab";
import RewriteTab from "../components/book/RewriteTab";
import VersionsTab from "../components/book/VersionsTab";
import ExportsTab from "../components/book/ExportsTab";
import EbookTab from "../components/book/EbookTab";
import AudiobookTab from "../components/book/AudiobookTab";
import CoversTab from "../components/book/CoversTab";
import StructureTab from "../components/book/StructureTab";
import WriteTab from "../components/book/WriteTab";
import PreviewTab from "../components/book/PreviewTab";
import { PremiumGate } from "./BillingPage";
import ResearchTab from "../components/book/ResearchTab";
import PrintTab from "../components/book/PrintTab";
import PictureTab from "../components/book/PictureTab";
import InsightsTab from "../components/book/InsightsTab";
import PreflightTab from "../components/book/PreflightTab";
import ShowcaseTab from "../components/book/ShowcaseTab";
import ComicSetupTab from "../components/comic/ComicSetupTab";
import ComicPagesTab from "../components/comic/ComicPagesTab";
import ComicEbookTab from "../components/comic/ComicEbookTab";
import ComicPrintTab from "../components/comic/ComicPrintTab";
import WebtoonExportTab from "../components/comic/WebtoonExportTab";
import PoemsTab from "../components/poetry/PoemsTab";
import CollectionTab from "../components/poetry/CollectionTab";
import { ProgressPanel } from "../components/book/ProgressBars";
import PipelineTab from "../components/book/PipelineTab";
import { GuidedTabs } from "../components/book/GuidedTabs";
import { TABS, tabsFor } from "../lib/bookTabs";
import { setCurrentBook } from "../lib/currentBook";

const FORMAT_LABEL = { comic: "Comic book", manga: "Manga", webtoon: "Webtoon", poetry: "Poetry collection" };

function FormatTabs({ book, tab, setTab, props }) {
  const tabs = tabsFor(book.format);
  const cur = tabs.some(([k]) => k === tab) ? tab : tabs[0][0];
  return (
    <Tabs value={cur} onValueChange={setTab}>
      <GuidedTabs tabs={tabs} tab={cur} />
      {book.format === "poetry" ? (
        <>
          <TabsContent value="poems"><PoemsTab {...props} /></TabsContent>
          <TabsContent value="collection"><CollectionTab {...props} /></TabsContent>
        </>
      ) : (
        <>
          <TabsContent value="pages"><ComicPagesTab {...props} /></TabsContent>
          <TabsContent value="setup"><ComicSetupTab {...props} /></TabsContent>
          <TabsContent value="ebook"><ComicEbookTab {...props} /></TabsContent>
          <TabsContent value="print"><ComicPrintTab {...props} /></TabsContent>
          <TabsContent value="export"><WebtoonExportTab {...props} /></TabsContent>
        </>
      )}
      <TabsContent value="covers"><PremiumGate><CoversTab {...props} /></PremiumGate></TabsContent>
      <TabsContent value="showcase"><ShowcaseTab {...props} /></TabsContent>
    </Tabs>
  );
}

export default function BookDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [book, setBook] = useState(null);
  const [version, setVersion] = useState(null);
  const [missing, setMissing] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/books/${id}`);
      setBook(data); setCurrentBook(data);
      if (data.current_version_id) {
        const v = await api.get(`/books/${id}/versions/${data.current_version_id}`);
        setVersion(v.data);
      } else setVersion(null);
    } catch (e) { if (e.response?.status === 404) setMissing(true); else toast.error(errMsg(e)); }
  }, [id]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => () => setCurrentBook(null), []);
  useEffect(() => { window.addEventListener("book-reload", reload); return () => window.removeEventListener("book-reload", reload); }, [reload]);

  if (missing) return <div className="panel p-12 text-center" data-testid="book-not-found"><p className="font-display text-2xl mb-3">Book not found</p><p className="text-sm text-slate-400 mb-6">It may have been archived, or you don't have access to it.</p><Link to="/app" className="text-amber-400" data-testid="not-found-back">Back to My Books</Link></div>;
  if (!book) return <p className="text-slate-500">Loading…</p>;
  const props = { book, version, reload };
  if (FORMAT_LABEL[book.format]) return (
    <div>
      <Link to="/app" className="text-xs text-slate-500 hover:text-amber-300 inline-flex items-center gap-1 mb-4" data-testid="back-to-library"><ArrowLeft size={14} />My Books</Link>
      <div className="mb-8">
        <p className="eyebrow mb-2" data-testid="book-format-label">{FORMAT_LABEL[book.format]}{book.series_name ? ` · ${book.series_name}` : ""}</p>
        <h1 className="font-display text-4xl sm:text-5xl" data-testid="book-title">{book.title}</h1>
      </div>
      <ProgressPanel bookId={book.id} onGo={(t) => setParams({ tab: t })} refreshKey={`${book.updated_at}-${tab}`} />
      <FormatTabs book={book} tab={tab} setTab={(t) => setParams({ tab: t })} props={props} />
    </div>
  );
  return (
    <div>
      <Link to="/app" className="text-xs text-slate-500 hover:text-amber-300 inline-flex items-center gap-1 mb-4" data-testid="back-to-library"><ArrowLeft size={14} />My Books</Link>
      <div className="mb-8">
        {book.series_name && <p className="eyebrow mb-2">{book.series_name}{book.book_number != null ? ` · Book ${book.book_number}` : ""}</p>}
        <h1 className="font-display text-4xl sm:text-5xl" data-testid="book-title">{book.title}</h1>
        <p className="text-slate-400 mt-2 text-sm" data-testid="book-current-version">
          {version ? `Current manuscript: v${version.number} — ${version.label} · ${version.word_count.toLocaleString()} words · ${version.chapter_count} sections` : "No manuscript imported yet"}
        </p>
      </div>
      <ProgressPanel bookId={book.id} onGo={(t) => setParams({ tab: t })} refreshKey={`${book.updated_at}-${tab}`} />
      <Tabs value={tab} onValueChange={(t) => setParams({ tab: t })}>
        <GuidedTabs tabs={TABS} tab={tab} />
        <TabsContent value="overview"><OverviewTab {...props} /></TabsContent>
        <TabsContent value="upload"><UploadTab {...props} /></TabsContent>
        <TabsContent value="manuscript"><ManuscriptTab {...props} /></TabsContent>
        <TabsContent value="structure"><StructureTab {...props} /></TabsContent>
        <TabsContent value="write"><WriteTab {...props} /></TabsContent>
        <TabsContent value="preview"><PreviewTab {...props} /></TabsContent>
        <TabsContent value="pipeline"><PremiumGate><PipelineTab {...props} /></PremiumGate></TabsContent>
        <TabsContent value="proofread"><PremiumGate><ProofreadTab {...props} /></PremiumGate></TabsContent>
        <TabsContent value="rewrite"><PremiumGate><RewriteTab {...props} /></PremiumGate></TabsContent>
        <TabsContent value="versions"><VersionsTab {...props} /></TabsContent>
        <TabsContent value="insights"><PremiumGate><InsightsTab {...props} /></PremiumGate></TabsContent>
        <TabsContent value="showcase"><ShowcaseTab {...props} /></TabsContent>
        <TabsContent value="preflight"><PreflightTab {...props} /></TabsContent>
        <TabsContent value="paperback"><PrintTab key="paperback" edition="paperback" {...props} /></TabsContent>
        <TabsContent value="hardcover"><PrintTab key="hardcover" edition="hardcover" {...props} /></TabsContent>
        <TabsContent value="picture"><PictureTab {...props} /></TabsContent>
        <TabsContent value="ebook"><EbookTab {...props} /></TabsContent>
        <TabsContent value="audiobook"><PremiumGate><AudiobookTab {...props} /></PremiumGate></TabsContent>
        <TabsContent value="covers"><PremiumGate><CoversTab {...props} /></PremiumGate></TabsContent>
        <TabsContent value="research"><PremiumGate><ResearchTab {...props} /></PremiumGate></TabsContent>
        <TabsContent value="exports"><ExportsTab {...props} /></TabsContent>
      </Tabs>
    </div>
  );
}
